import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/common/DashboardLayout';
import { getTodayCheckin, submitCheckin } from '../../services/checkin.service';
import { fireHearts, fireConfetti } from '../../utils/celebrate';
import { getScoreSummary }               from '../../services/scores.service';
import { getResetPath, getMemberFocus, setFocusMilestone, updateMemberFocus } from '../../services/resetPath.service';
import { getTodayCompletions, toggleCompletion } from '../../services/nonNegotiable.service';
import { syncGoogleTasksNow } from '../../services/integration.service';
import { getTodayBlocks, toggleCompletion as toggleBlockCompletion } from '../../services/idealAgenda.service';
import { markMilestoneReached, unmarkMilestoneReached } from '../../services/goals.service';
import { getAnnouncement }               from '../../services/hubAnnouncement.service';
import { getRunningContests }           from '../../services/community.service';
import { getMyHubEvents, getUpcomingHubEvents, updateMemberPreferences } from '../../services/hubEvents.service';
import * as courseService                from '../../services/course.service';
import { getMyDayType, setMyDayType }    from '../../services/membership.service';
import { getMyCalendarEvents }           from '../../services/integration.service';
import { formatDuration }                from '../../utils/duration';
import MentionsBar from '../../components/dashboard/MentionsBar';
import WelcomeCard from '../../components/dashboard/WelcomeCard';
import AffiliateWaitingBar from '../../components/dashboard/AffiliateWaitingBar';

// ─── Constants ────────────────────────────────────────────────────────────────

const SECTOR_META = {
  financial: { label: 'Financial',  emoji: '💰', dot: '#FBBF24' },
  career:    { label: 'Career',     emoji: '💼', dot: '#60A5FA' },
  emotional: { label: 'Emotional',  emoji: '💛', dot: '#F472B6' },
  mental:    { label: 'Mental',     emoji: '🧠', dot: '#A78BFA' },
  physical:  { label: 'Physical',   emoji: '💪', dot: '#34D399' },
  spiritual: { label: 'Spiritual',  emoji: '🌿', dot: '#818CF8' },
};

const SECTORS_DEFAULT_ORDER = ['financial', 'career', 'emotional', 'mental', 'physical', 'spiritual'];

const MOOD_EMOJIS = {
  sad:     '😢',
  angry:   '😡',
  afraid:  '😨',
  anxious: '😰',
  ok:      '😑', // was "meh" until 2026-09-30 (migration 146)
  content: '😊',
  happy:   '😄',
  joyous:  '🤩',
};

const ACTIVITIES = [
  { key: 'cardio',           label: 'Cardio',    emoji: '🏃' },
  { key: 'strength',         label: 'Strength',  emoji: '💪' },
  { key: 'stretching',       label: 'Stretch',   emoji: '🧘' },
  { key: 'writing_creating', label: 'Writing',   emoji: '✍️' },
  { key: 'work',             label: 'Work',      emoji: '💼' },
  { key: 'reading',          label: 'Reading',   emoji: '📚' },
  { key: 'pro_development',  label: 'Pro Dev',   emoji: '📈' },
  { key: 'meditation',       label: 'Meditate',  emoji: '🧠' },
  { key: 'prayer',           label: 'Prayer',    emoji: '🙏' },
  { key: 'hobby',            label: 'Hobby',     emoji: '🎨' },
  { key: 'connecting',       label: 'Connect',   emoji: '🤝' },
];

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

/** YYYY-MM-DD in the browser's local timezone (not UTC). */
function todayKey() {
  return new Date().toLocaleDateString('en-CA'); // en-CA formats as YYYY-MM-DD
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  try {
    const [h, m] = timeStr.split(':').map(Number);
    const ampm = h >= 12 ? 'pm' : 'am';
    const hour = h % 12 || 12;
    return `${hour}:${String(m).padStart(2, '0')}${ampm}`;
  } catch {
    return timeStr;
  }
}

// ─── Compact check-in sub-components ─────────────────────────────────────────

function Chip({ children, active, onClick, title }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 min-h-[44px] px-3 py-2 rounded-xl text-sm transition select-none
        ${active
          ? 'bg-amber-100 text-amber-800 border border-amber-300'
          : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'}`}
    >
      {children}
    </button>
  );
}

function SleepPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <Chip active={value !== ''} onClick={() => setOpen(o => !o)} title="Sleep hours">
        <span className="text-xl leading-none">🌙</span> {value !== '' ? `${value}h` : '—'}
      </Chip>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-20 bg-white border border-gray-200 rounded-xl shadow-lg p-2 flex flex-wrap gap-1.5 w-64">
          <p className="w-full text-xs font-medium text-gray-500 px-1 pb-1">Select sleep hours</p>
          {[...Array(25)].map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { onChange(i); setOpen(false); }}
              className={`w-11 h-11 rounded-lg text-sm font-medium transition
                ${value === i ? 'bg-amber-500 text-white' : 'bg-gray-100 hover:bg-amber-100 text-gray-700'}`}
            >
              {i}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EnergyPicker({ value, onChange }) {
  return (
    <div className="inline-flex items-center gap-1.5 min-h-[44px] px-3 py-2 rounded-xl border border-gray-200 bg-gray-50">
      <span className="text-xl leading-none mr-0.5">⚡</span>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n === value ? 0 : n)}
          className={`w-7 h-7 rounded-full transition ${n <= value ? 'bg-amber-500' : 'bg-gray-300'}`}
          title={`${n} energy`}
          aria-label={`Energy ${n} of 5`}
        />
      ))}
      <span className="text-xs text-gray-500 ml-1">energy</span>
    </div>
  );
}

function MoodPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <Chip active={!!value} onClick={() => setOpen(o => !o)} title="Mood">
        <span className="text-xl leading-none">{value ? MOOD_EMOJIS[value] : '😐'}</span> {value || 'mood'}
      </Chip>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-20 bg-white border border-gray-200 rounded-xl shadow-lg p-2 flex gap-1.5 flex-wrap w-60">
          <p className="w-full text-xs font-medium text-gray-500 px-1 pb-1">How are you feeling?</p>
          {Object.entries(MOOD_EMOJIS).map(([mood, emoji]) => (
            <button
              key={mood}
              type="button"
              onClick={() => { onChange(mood); setOpen(false); }}
              title={mood}
              className={`w-12 h-12 rounded-lg text-3xl transition
                ${value === mood ? 'bg-amber-100 border border-amber-300' : 'hover:bg-gray-100'}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// What each short vital label means, shown when it's tapped (Jenn, 2026-09-29: "so people know what the icons mean").
const VITAL_NAMES = { BS: 'Blood sugar', BP: 'Blood pressure', HR: 'Heart rate' };

function VitalInput({ icon, label, value, onChange }) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  return (
    <div className="relative">
      <Chip active={!!value} onClick={() => setOpen(o => !o)} title={label}>
        <span className="text-xl leading-none">{icon}</span> {value || label}
      </Chip>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-20 bg-white border border-gray-200 rounded-xl shadow-lg p-2 w-44">
          <p className="text-xs font-medium text-gray-500 px-1 pb-1">{VITAL_NAMES[label] || label}</p>
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={e => onChange(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && setOpen(false)}
            placeholder={label}
            className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-400"
          />
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-1 w-full text-xs text-amber-600 font-medium"
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}

function MealsPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <Chip active={!!value} onClick={() => setOpen(o => !o)} title="Meals">
        <span className="text-xl leading-none">🍽️</span> {value || '—'}
      </Chip>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-20 bg-white border border-gray-200 rounded-xl shadow-lg p-2 flex flex-wrap gap-2 w-48">
          <p className="w-full text-xs font-medium text-gray-500 px-1 pb-1">How many meals today?</p>
          {['1', '2', '3+'].map(v => (
            <button
              key={v}
              type="button"
              onClick={() => { onChange(v); setOpen(false); }}
              className={`min-w-[48px] min-h-[44px] px-3 py-2 rounded-lg text-base font-medium transition
                ${value === v ? 'bg-amber-500 text-white' : 'bg-gray-100 hover:bg-amber-100 text-gray-700'}`}
            >
              {v}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function WaterPicker({ value, onChange }) {
  return (
    <div className="inline-flex items-center gap-1 flex-wrap min-h-[44px] px-3 py-2 rounded-xl border border-gray-200 bg-gray-50">
      <span className="text-xl leading-none mr-0.5">💧</span>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n === value ? n - 1 : n)}
          className={`w-6 h-6 rounded-full transition ${n <= value ? 'bg-blue-400' : 'bg-gray-300'}`}
          title={`${n} glasses`}
          aria-label={`${n} glasses of water`}
        />
      ))}
      <span className="text-xs text-gray-500 ml-1">glasses</span>
    </div>
  );
}

function ActivityToggle({ activity, active, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={activity.label}
      className={`inline-flex flex-col items-center justify-center gap-1 min-w-[64px] min-h-[56px] px-2.5 py-2 rounded-xl text-xs border transition
        ${active
          ? 'bg-amber-100 text-amber-800 border-amber-300'
          : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'}`}
    >
      <span className="text-2xl leading-none">{activity.emoji}</span>
      <span className="text-xs leading-none">{activity.label}</span>
    </button>
  );
}

// ─── Compass Check-In Strip ───────────────────────────────────────────────────

// ─── What's Next Card (ADHD simplified view) ─────────────────────────────────

function WhatNextCard({ items, onToggleNN, onToggleBlock }) {
  const [showMore, setShowMore] = useState(false);
  const total      = items.length;
  const doneCount  = items.filter(i => i.completed).length;
  const incomplete = items.filter(i => !i.completed);
  const current    = incomplete[0] || null;
  const next       = incomplete[1] || null;
  useEffect(() => { setShowMore(false); }, [current?.id]);

  if (total === 0) return null;

  if (!current) {
    return (
      <div className="text-center py-4 space-y-2">
        <div className="text-4xl">🎉</div>
        <p className="font-semibold text-gray-800">All done for today!</p>
        <p className="text-xs text-gray-400">{total} of {total} complete</p>
      </div>
    );
  }

  const handleToggle = () => {
    if (current.type === 'block') {
      onToggleBlock({ id: current.rawId, completed: current.completed });
    } else {
      onToggleNN({ id: current.rawId, completed: current.completed });
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-400 text-center">{doneCount} of {total} done today</p>
      <div
        onClick={handleToggle}
        className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100 cursor-pointer hover:bg-amber-50 hover:border-amber-200 transition group"
      >
        <div className={`w-12 h-12 rounded-full border-2 flex items-center justify-center shrink-0 transition
          ${current.completed
            ? 'border-amber-400 bg-amber-400 text-white'
            : 'border-amber-300 group-hover:border-amber-400'}`}
        >
          {current.completed && <span className="text-xl">✓</span>}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-base font-semibold leading-snug ${current.completed ? 'line-through text-gray-400' : 'text-gray-800'}`}>
            {current.text}
          </p>
          {current.time && <p className="text-xs text-gray-400 mt-0.5">{current.time}</p>}
        </div>
      </div>
      {/* Same "more" as the full list. Outside the card, so reading it never ticks the item off. */}
      {current.description && (
        <div className="px-1">
          <button
            type="button"
            onClick={() => setShowMore(v => !v)}
            aria-expanded={showMore}
            className="text-xs text-reset-terracotta hover:underline"
          >
            {showMore ? 'less' : 'more'}
          </button>
          {showMore && <p className="text-sm text-gray-600 whitespace-pre-line mt-1">{current.description}</p>}
        </div>
      )}
      {next && (
        <p className="text-xs text-gray-400 text-center">Next up: {next.text}</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function CompassStrip({ onLoggedSuccess }) {
  const [checkinDone, setCheckinDone]   = useState(null);
  const [editMode, setEditMode]         = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState('');
  const [form, setForm] = useState({
    sleep_hours:       '',
    energy_level:      0,
    mood:              '',
    blood_sugar:       '',
    blood_pressure:    '',
    heart_rate:        '',
    meals_eaten:       '',
    water_glasses:     0,
    activities: {
      cardio: false, strength: false, stretching: false, writing_creating: false,
      work: false, reading: false, pro_development: false, meditation: false,
      prayer: false, hobby: false, connecting: false,
    },
  });

  useEffect(() => {
    getTodayCheckin()
      .then(d => {
        setCheckinDone(!!d.checkin);
        if (d.checkin) {
          const c = d.checkin;
          // Convert stored integer meals_eaten back to picker format (1→'1', 2→'2', 3→'3+')
          const mealsVal = c.meals_eaten >= 3 ? '3+' : c.meals_eaten ? String(c.meals_eaten) : '';
          setForm({
            sleep_hours:    c.sleep_hours    ?? '',
            energy_level:   c.energy_level   ?? 0,
            mood:           c.mood           || '',
            blood_sugar:    c.blood_sugar    || '',
            blood_pressure: c.blood_pressure || '',
            heart_rate:     c.heart_rate     || '',
            meals_eaten:    mealsVal,
            water_glasses:  c.water_intake   ?? 0,
            activities: {
              cardio:           !!c.cardio,
              strength:         !!c.strength,
              stretching:       !!c.stretching,
              writing_creating: !!c.writing_creating,
              work:             !!c.business_work,
              reading:          !!c.reading,
              pro_development:  !!c.professional_development,
              meditation:       !!c.meditation,
              prayer:           !!c.prayer_downtime,
              hobby:            !!c.hobby,
              connecting:       !!c.connection,
            },
          });
        }
      })
      .catch(() => setCheckinDone(false));
  }, []);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const setActivity = (key) => setForm(prev => ({
    ...prev,
    activities: { ...prev.activities, [key]: !prev.activities[key] },
  }));

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await submitCheckin({
        checkin_date:           todayKey(),
        sleep_hours:            Number(form.sleep_hours) || 0,
        energy_level:           form.energy_level,
        mood:                   form.mood || null,
        blood_sugar:            form.blood_sugar || null,
        blood_pressure:         form.blood_pressure || null,
        heart_rate:             form.heart_rate ? Number(form.heart_rate) : null,
        meals_eaten:            form.meals_eaten || null,
        water_glasses:          form.water_glasses,
        did_cardio:             form.activities.cardio,
        did_strength:           form.activities.strength,
        did_stretching:         form.activities.stretching,
        did_writing_creating:   form.activities.writing_creating,
        did_work:               form.activities.work,
        did_reading:            form.activities.reading,
        did_pro_development:    form.activities.pro_development,
        did_meditation:         form.activities.meditation,
        did_prayer:             form.activities.prayer,
        did_hobby:              form.activities.hobby,
        did_connecting:         form.activities.connecting,
      });
      setCheckinDone(true);
      setEditMode(false);
      fireConfetti(); // Jenn, 2026-09-29: confetti when the daily Compass check-in is logged
      if (onLoggedSuccess) onLoggedSuccess();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save your check-in.');
    } finally {
      setSubmitting(false);
    }
  };

  const isDone = checkinDone && !editMode;

  /** Open the form with all fields cleared for a brand-new check-in entry. */
  const handleStartFresh = () => {
    setForm({
      sleep_hours: '', energy_level: 0, mood: '',
      blood_sugar: '', blood_pressure: '', heart_rate: '',
      meals_eaten: '', water_glasses: 0,
      activities: {
        cardio: false, strength: false, stretching: false, writing_creating: false,
        work: false, reading: false, pro_development: false, meditation: false,
        prayer: false, hobby: false, connecting: false,
      },
    });
    setEditMode(true);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          <img src="/compass-icon.png" alt="" className="w-4 h-4" /> Compass Check-In
        </p>
        {isDone && (
          <div className="flex items-center gap-3">
            <button onClick={handleStartFresh} className="text-xs text-gray-400 hover:text-gray-600 hover:underline">
              Start fresh
            </button>
            <button onClick={() => setEditMode(true)} className="text-xs text-amber-600 hover:underline">
              Update →
            </button>
          </div>
        )}
      </div>

      {isDone ? (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-green-600 font-medium">✓ Logged today</span>
          <div className="flex items-center gap-1.5 flex-wrap text-xs text-gray-500">
            {form.sleep_hours !== '' && <span>🌙 {form.sleep_hours}h</span>}
            {form.energy_level > 0 && <span>⚡ {form.energy_level}/5</span>}
            {form.mood && <span>{MOOD_EMOJIS[form.mood]}</span>}
            {form.meals_eaten && <span>🍽️ {form.meals_eaten}</span>}
            {form.water_glasses > 0 && <span>💧 {form.water_glasses}</span>}
            {Object.values(form.activities).some(Boolean) && (
              <span>
                {ACTIVITIES.filter(a => form.activities[a.key]).map(a => a.emoji).join(' ')}
              </span>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <SleepPicker value={form.sleep_hours} onChange={v => set('sleep_hours', v)} />
            <EnergyPicker value={form.energy_level} onChange={v => set('energy_level', v)} />
            <MoodPicker value={form.mood} onChange={v => set('mood', v)} />
            <VitalInput icon="🩸" label="BS" value={form.blood_sugar} onChange={v => set('blood_sugar', v)} />
            <VitalInput icon="💓" label="BP" value={form.blood_pressure} onChange={v => set('blood_pressure', v)} />
            <VitalInput icon="❤️" label="HR" value={form.heart_rate} onChange={v => set('heart_rate', v)} />
            <MealsPicker value={form.meals_eaten} onChange={v => set('meals_eaten', v)} />
            <WaterPicker value={form.water_glasses} onChange={v => set('water_glasses', v)} />
            {ACTIVITIES.map(a => (
              <ActivityToggle
                key={a.key}
                activity={a}
                active={form.activities[a.key]}
                onToggle={() => setActivity(a.key)}
              />
            ))}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="inline-flex items-center gap-1 min-h-[44px] px-5 py-2.5 rounded-xl text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 transition disabled:opacity-60"
            >
              {submitting ? '…' : '✓ Log'}
            </button>
          </div>
          {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        </>
      )}

      {/* Below the daily entry, not above it — Jenn, 2026-08-31: when showing
          Patti this page, her instinct was to click the quarterly link
          before ever touching the daily check-in it sits next to. */}
      <Link to="/dashboard/quarterly-checkin" className="text-xs text-gray-400 hover:text-amber-600 hover:underline block mt-3 pt-3 border-t border-gray-100">
        Quarterly Compass Check-in — a deeper look once a quarter →
      </Link>
    </div>
  );
}

// ─── Sector Zone Card — Two-bar redesign ─────────────────────────────────────
//
// Bar 1 (Where I am): 3 zones — Rock Bottom | Middle Ground | Overabundance
//   Position = % of middle_ground items answered 'yes'
//   (assessment only covers middle_ground and comfort_zone items)
//
// Bar 2 (Goal Progress): 4 zones — Not started | Working on | In my zone | Excelled
//   Progress = % of comfort_zone items answered 'yes'

function SectorZoneCard({ sector, resetPath }) {
  const meta = SECTOR_META[sector];

  // Comfort-zone/position columns default to '{}' (empty JSONB object) in the
  // database for a member who's never filled these in, not '[]' or null — a
  // plain `|| []` fallback doesn't catch that since {} is truthy, so these
  // silently held an object instead of an array and crashed every .forEach/
  // .filter/.length call below the instant a real member (not a fresh test
  // account with no reset_paths row at all) hit this component. Confirmed
  // 2026-08-31 against a live member account mid-onboarding.
  const asArray = (v) => Array.isArray(v) ? v : [];
  const comfortZoneItems  = asArray(resetPath?.[`${sector}_comfort_zone_items`]);
  const middleGroundItems = asArray(resetPath?.[`${sector}_middle_ground_items`]);

  // Build answer map: itemId → 'yes' | 'no'
  const posItems = asArray(resetPath?.[`${sector}_current_position_items`]);
  const posMap   = {};
  posItems.forEach(p => { if (p.item_id) posMap[p.item_id] = p.answer; });

  // ── Bar 1: Spectrum position ────────────────────────────────────────────────
  // Primary: use middle ground items (rock bottom → goal spectrum).
  // Fallback: if no middle ground items are set up, use comfort zone items
  // so sectors where only goal items were defined still show a position dot.
  const effectiveItems = middleGroundItems.length > 0 ? middleGroundItems : comfortZoneItems;
  const mgAnswered  = effectiveItems.filter(it => posMap[it.id] !== undefined);
  const mgYes       = effectiveItems.filter(it => posMap[it.id] === 'yes').length;
  const mgTotal     = effectiveItems.length;
  const hasSpectrum = mgTotal > 0 && mgAnswered.length > 0;
  const spectrumPct = hasSpectrum ? Math.round((mgYes / mgTotal) * 100) : null;

  function spectrumDotColor(pct) {
    if (pct < 25) return '#f87171';  // red   = rock bottom
    if (pct < 75) return '#60a5fa';  // blue  = middle ground
    return '#fb923c';                // orange = overabundance
  }

  // ── Bar 2: Comfort zone / goal progress ─────────────────────────────────────
  const czYes  = comfortZoneItems.filter(it => posMap[it.id] === 'yes').length;
  const czTotal = comfortZoneItems.length;
  const hasCZ  = czTotal > 0;
  const czPct  = hasCZ ? Math.round((czYes / czTotal) * 100) : null;

  // Map pct → position along 4-zone bar (0–100%)
  function czBarPos(pct) {
    if (pct === 0) return 12.5;  // Not started
    if (pct < 34)  return 37.5;  // Working on
    if (pct < 67)  return 62.5;  // In my zone
    return 87.5;                 // Excelled
  }

  const hasAnyItems = comfortZoneItems.length > 0 || middleGroundItems.length > 0;

  return (
    <Link
      to={`/dashboard/continuums#${sector}`}
      className="block bg-white rounded-xl border border-gray-200 p-3 hover:border-amber-300 hover:shadow-sm transition-all"
    >
      {/* Header */}
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-base">{meta.emoji}</span>
        <span className="text-xs font-semibold text-gray-700">{meta.label}</span>
      </div>

      {!hasAnyItems ? (
        <span className="text-[11px] text-amber-600">
          Define my {meta.label.toLowerCase()} goal →
        </span>
      ) : (
        <>
          {/* Bar 1: Spectrum position */}
          <div className="mb-2">
            <p className="text-[8px] text-gray-400 uppercase tracking-wide mb-0.5">Where I am</p>
            <div className="relative">
              <div className="flex h-2.5 rounded-sm overflow-hidden border border-gray-200">
                <div className="flex-1 bg-red-100" />
                <div className="flex-1 bg-blue-100 border-x border-gray-200" />
                <div className="flex-1 bg-orange-100" />
              </div>
              {spectrumPct !== null && (
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full border-2 border-white shadow-sm"
                  style={{ backgroundColor: spectrumDotColor(spectrumPct), left: `calc(${spectrumPct}% - 5px)` }}
                />
              )}
            </div>
            <div className="flex mt-0.5">
              <div className="flex-1 text-center text-[7px] text-gray-400 leading-tight">Rock Bottom</div>
              <div className="flex-1 text-center text-[7px] text-gray-400 leading-tight">Middle Ground</div>
              <div className="flex-1 text-center text-[7px] text-gray-400 leading-tight">Overabundance</div>
            </div>
          </div>

          {/* Bar 2: Goal progress */}
          {hasCZ && (
            <div className="mb-2">
              <p className="text-[8px] text-gray-400 uppercase tracking-wide mb-0.5">Goal Progress</p>
              <div className="relative">
                <div className="flex h-2.5 rounded-sm overflow-hidden border border-gray-200">
                  <div className="flex-1 bg-gray-100" />
                  <div className="flex-1 bg-yellow-100 border-x border-gray-200" />
                  <div className="flex-1 bg-green-100 border-r border-gray-200" />
                  <div className="flex-1 bg-emerald-200" />
                </div>
                {czPct !== null && (
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full border-2 border-white shadow-sm"
                    style={{ backgroundColor: '#4ade80', left: `calc(${czBarPos(czPct)}% - 5px)` }}
                  />
                )}
              </div>
              <div className="flex mt-0.5">
                <div className="flex-1 text-center text-[7px] text-gray-400 leading-tight">Not started</div>
                <div className="flex-1 text-center text-[7px] text-gray-400 leading-tight">Working on</div>
                <div className="flex-1 text-center text-[7px] text-gray-400 leading-tight">In my zone</div>
                <div className="flex-1 text-center text-[7px] text-gray-400 leading-tight">Excelled</div>
              </div>
            </div>
          )}

          {/* All comfort zone items with yes/no indicator */}
          {comfortZoneItems.length > 0 && (
            <div className="border-t border-gray-100 pt-1.5 mt-0.5 space-y-0.5">
              {comfortZoneItems.map(item => {
                const isYes = posMap[item.id] === 'yes';
                return (
                  <div key={item.id} className="flex items-start gap-1">
                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-0.5 ${
                      isYes ? 'bg-green-400' : 'bg-gray-200'
                    }`} />
                    <span className={`text-[9px] leading-tight ${
                      isYes ? 'text-gray-600' : 'text-gray-400'
                    }`}>
                      {item.text}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </Link>
  );
}

// ─── "We haven't seen you" — shown once when a paying member hasn't opened
// the app in 2+ weeks (server computes this from last_active_at, see
// jwt.js's sendTokens). Deliberately no guilt, no urgency — just an honest
// "life happens" and a soft path back in, matching Jenn's exact framing.

function ReengagementBubble({ firstName }) {
  return (
    <div className="bg-reset-sage/10 border border-reset-sage/30 rounded-2xl p-4 flex items-center gap-3 -mb-2">
      <span className="text-2xl shrink-0">🌿</span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-reset-brown">
          Hey {firstName}, it's been a bit — and we get it, life happens.
        </p>
        <p className="text-xs text-gray-500 mt-0.5">
          Whenever it feels right, pop into{' '}
          <Link to="/dashboard/events" className="text-reset-terracotta font-medium hover:underline">an event or a replay</Link>
          {' '}— no pressure, just here when you're ready.
        </p>
      </div>
    </div>
  );
}

// Small picture of the thing a home page bubble mentions (Jenn, 2026-10-14) —
// shown only when that thing has a picture. Alt text: the picture's own, else its title.
// wide: course pictures are 16:9 (Jenn, 2026-10-27: the New this week picture
// looked "scrunched" cut down to a small square). Oct 29: events too, and wide
// thumbnails show the whole picture (object-contain), never a cropped part; a square
// picture sits on a soft terracotta rectangle (Jenn, Oct 29).
// stacked (Oct 29, Jenn: "doing something weird"): in the narrow top bubbles a wide picture beside the words
// stretched to the bubble's full height and squeezed the words into a thin column, so there it goes full
// width ON TOP of the words instead. self-start keeps any picture from stretching to the row's height.
// Oct 29 later (Jenn: "they look unbalanced"): all four top bubbles stack, so every picture is the same
// box in the same place and the words line up across the row.
function BubbleThumb({ src, alt, wide = false, stacked = false }) {
  if (!src) return null;
  const size = stacked ? 'w-full aspect-video object-contain bg-reset-terracotta/10'
    : wide ? 'w-32 aspect-video object-contain bg-reset-terracotta/10' : 'w-14 h-14 object-cover bg-gray-100';
  return <img src={src} alt={alt || ''} className={`${size} rounded-lg shrink-0 self-start`} loading="lazy" />;
}

// ─── Contest — a running members-only contest (Jenn, 2026-10-24) ──────────────
// Only there while a contest runs; opens the contest's My Hive post.

function ContestBubble({ contest }) {
  const to = contest.hivePostId ? `/dashboard/discussion/${contest.hivePostId}` : '/dashboard/discussion';
  const ends = contest.endsOn
    ? new Date(`${contest.endsOn}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : null;
  return (
    <div className="bg-white rounded-2xl border border-reset-terracotta/40 p-4 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-base leading-none" aria-hidden="true">🏆</span>
        <h3 className="font-semibold text-gray-800 text-sm">Contest</h3>
      </div>
      <div className="flex flex-col gap-2 flex-1">
        <BubbleThumb src={contest.imageUrl} alt={contest.imageAlt || contest.name} stacked />
        <div>
          <p className="text-sm font-medium text-reset-brown leading-snug">{contest.name}</p>
          {contest.message && <p className="text-xs text-gray-600 mt-1 leading-snug">{contest.message}</p>}
          {ends && <p className="text-[11px] text-gray-400 mt-1">Until {ends}</p>}
        </div>
      </div>
      <Link to={to} className="text-xs font-medium text-reset-terracotta hover:underline mt-3">
        See the contest →
      </Link>
    </div>
  );
}

// ─── New this week — courses published (or lessons added) in the last 7 days ──
// Jenn, 2026-10-26: a horizontal bubble under the top cards, only there when something new went live.
// Coming-soon courses never show here; the server decides what counts as new.

function NewThisWeekBubble({ items }) {
  return (
    <div className="bg-white rounded-2xl border border-reset-sage/40 p-4 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-base leading-none" aria-hidden="true">✨</span>
        <h3 className="font-semibold text-gray-800 text-sm">New this week</h3>
      </div>
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4">
        {items.map(it => (
          <Link key={it.key} to={it.to} className="flex gap-3 items-center md:w-[calc(50%-0.5rem)] xl:w-[calc(33.333%-0.7rem)] group">
            <BubbleThumb src={it.image} alt={it.imageAlt} wide />
            <div className="min-w-0">
              <p className="text-sm font-medium text-reset-brown leading-snug group-hover:underline">{it.title}</p>
              {it.sub && <p className="text-xs text-gray-500 mt-0.5 leading-snug">{it.sub}</p>}
              <p className="text-xs font-medium text-reset-terracotta mt-1">{it.cta} →</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ─── Thought for the Day — today's Hub Announcement, title-only ───────────────

function ThoughtForTheDayBubble({ announcement }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <img src="/hive-icon.png" alt="" className="w-5 h-5" />
        <h3 className="font-semibold text-gray-800 text-sm">Thought for the Day</h3>
      </div>
      {announcement ? (
        <>
          <div className="flex flex-col gap-2 flex-1">
            <BubbleThumb src={announcement.image_url} alt={announcement.image_alt_text || announcement.title} stacked />
            <p className="text-sm text-gray-700 leading-snug">
              {announcement.title || announcement.message_text}
            </p>
          </div>
          <Link
            to={`/dashboard/announcements/${announcement.id}`}
            className="text-xs font-medium text-reset-terracotta hover:underline mt-3"
          >
            Read more →
          </Link>
        </>
      ) : (
        <p className="text-sm text-gray-400 italic">Nothing new today — check back soon.</p>
      )}
    </div>
  );
}

// ─── Learning Lab — resume the most recently opened course ───────────────────

function LearningLabBubble({ course }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <img src="/learning-lab-icon.png" alt="" className="w-5 h-5" />
        <h3 className="font-semibold text-gray-800 text-sm">Learning Lab</h3>
      </div>
      {course && course.next_lesson ? (
        <>
          <div className="flex flex-col gap-2 flex-1">
            <BubbleThumb src={course.next_lesson.lesson_image_url || course.cover_image_url}
              alt={course.next_lesson.lesson_image_url ? course.next_lesson.title : course.title} stacked />
            <div>
              <p className="text-sm text-gray-700 leading-snug">
                {course.completed_count > 0 ? 'Continue' : 'Start'}{' '}
                <span className="font-medium text-reset-brown">{course.next_lesson.title}</span>
                {course.next_lesson.video_duration_seconds > 0 && (
                  <span className="text-gray-400">, {formatDuration(course.next_lesson.video_duration_seconds)}</span>
                )}
              </p>
              <p className="text-xs text-gray-400 mt-1">{course.title}</p>
            </div>
          </div>
          <Link
            to={`/dashboard/learning-lab/${course.id}/${course.next_lesson.id}`}
            className="text-xs font-medium text-reset-terracotta hover:underline mt-3"
          >
            {course.completed_count > 0 ? 'Continue course' : 'Start course'} →
          </Link>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-400 italic flex-1">Jump into your 365reset courses.</p>
          <Link to="/dashboard/learning-lab" className="text-xs font-medium text-reset-terracotta hover:underline mt-3">
            Open Learning Lab →
          </Link>
        </>
      )}
    </div>
  );
}

// ─── Upcoming Events — next live Hub event, or this week's replays ───────────

function UpcomingEventsBubble({ nextEvent }) {
  const eventTimeLabel = (ev) => {
    const raw = ev?.event_datetime || ev?.event_date || ev?.scheduled_date;
    if (!raw) return null;
    return new Date(raw).toLocaleDateString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
      ...(ev.event_datetime ? { hour: 'numeric', minute: '2-digit' } : {}),
    });
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <img src="/events-icon.png" alt="" className="w-5 h-5" />
        <h3 className="font-semibold text-gray-800 text-sm">Upcoming Events</h3>
      </div>
      {nextEvent ? (
        <>
          <div className="flex flex-col gap-2 flex-1">
            <BubbleThumb src={nextEvent.type_image_url} alt={nextEvent.type_image_alt_text || nextEvent.title} stacked />
            <div>
              <p className="text-sm text-gray-700 leading-snug">
                <span className="font-medium text-reset-brown">{nextEvent.title}</span>
              </p>
              {eventTimeLabel(nextEvent) && (
                <p className="text-xs text-gray-400 mt-1">{eventTimeLabel(nextEvent)}</p>
              )}
            </div>
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-400 italic flex-1">Nothing scheduled right now — check replays.</p>
      )}
      <Link to="/dashboard/events" className="text-xs font-medium text-reset-terracotta hover:underline mt-3">
        {nextEvent ? 'View all events →' : 'See replays →'}
      </Link>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { user, updateUser } = useAuth();
  const [adhdMode, setAdhdMode] = useState(user?.adhd_mode || false);
  const [openTapId, setOpenTapId] = useState(null); // which What's-on-tap item shows its description
  const firstName = (user?.name || '').split(' ')[0] || 'there';
  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  const [announcement, setAnnouncement]         = useState(null);
  const [todayHubActivities, setTodayHubActivities] = useState([]);
  const [nonNegotiables, setNonNegotiables] = useState([]);
  const [todayBlocks, setTodayBlocks]       = useState([]);
  const [scoreSummary, setScoreSummary]     = useState([]);
  const [resetPath, setResetPath]           = useState(null);
  const [focusMilestone, setFocusMilestone] = useState(null);
  const [memberFocus, setMemberFocus]       = useState(null);
  const [focusDraft, setFocusDraft]         = useState('');
  const [focusEditing, setFocusEditing]     = useState(false);
  const [focusSaving, setFocusSaving]       = useState(false);
  const [dataLoaded, setDataLoaded]         = useState(false);
  const [latestCourse, setLatestCourse]     = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [nextEvent, setNextEvent]           = useState(null);
  const [contest, setContest]               = useState(null);
  const [newThisWeek, setNewThisWeek]       = useState([]);

  // Red/Yellow/Green — Jenn, 2026-08-29: members choose the kind of day
  // they're having so "What's on Tap" only shows activities tagged for that
  // energy level (the ideal-agenda energy_day field already supported this
  // end to end — the Dashboard just never gave members a way to pick one).
  // Server-persisted as of 2026-09-15 (previously browser-localStorage-only,
  // which meant it wasn't visible across the member's own devices, and
  // wasn't checkable by Team365/Claude at all — Jenn relies on checking this
  // daily).
  const [dayType, setDayType] = useState(null);
  const DAY_TYPE_ENERGY = { red: '0.5', yellow: '2', green: '4' };
  const [showDayTypeInfo, setShowDayTypeInfo] = useState(false);
  // Guards against an earlier click's response landing after a later one's —
  // without this, clicking Green then quickly Yellow could show Green's
  // blocks if that request happened to resolve second, since both requests
  // race independently and neither knows about the other.
  const dayTypeRequestRef = useRef(0);

  // The first page load below runs before this answer arrives (dayType is
  // still null then), so once the saved colour is known, redo What's on Tap
  // for it — otherwise the button shows Yellow but the list is the
  // no-colour one. 2026-09-30.
  useEffect(() => {
    getMyDayType().then(async (saved) => {
      if (!saved) return;
      const requestId = ++dayTypeRequestRef.current;
      setDayType(saved);
      const [blocks, nns] = await Promise.all([getTodayBlocks(DAY_TYPE_ENERGY[saved]), getTodayCompletions(DAY_TYPE_ENERGY[saved])]);
      if (requestId === dayTypeRequestRef.current) {
        setTodayBlocks(blocks);
        setNonNegotiables(nns);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetDayType = async (type) => {
    const next = dayType === type ? null : type; // click again to clear
    setDayType(next);
    setMyDayType(next).catch(() => { /* non-critical — UI already updated optimistically */ });
    const requestId = ++dayTypeRequestRef.current;
    const energy = next ? DAY_TYPE_ENERGY[next] : undefined;
    try {
      // Non-negotiables carry their own energy_day too (see migration 090) —
      // both need refetching together, or a non-negotiable set to one color
      // would keep showing on every other color even though its matching
      // ideal-agenda block correctly disappeared.
      const [blocks, nns] = await Promise.all([getTodayBlocks(energy), getTodayCompletions(energy)]);
      if (requestId === dayTypeRequestRef.current) {
        setTodayBlocks(blocks);
        setNonNegotiables(nns);
      }
    } catch { /* non-critical — keeps whatever was already shown */ }
  };

  // A running members-only contest shows as a 4th bubble (Jenn, 2026-10-24); none = the usual three.
  useEffect(() => { getRunningContests().then(list => setContest(list[0] || null)).catch(() => {}); }, []);
  useEffect(() => {
    courseService.getNewThisWeek().then(({ courses = [], lessons = [] }) => setNewThisWeek([
      ...courses.map(c => ({
        key: `c-${c.id}`, title: c.title, image: c.cover_image_url, imageAlt: c.title,
        sub: c.instructor_name ? `New course with ${c.instructor_name}` : 'New course',
        to: c.first_lesson_id ? `/dashboard/learning-lab/${c.id}/${c.first_lesson_id}` : '/dashboard/learning-lab',
        cta: 'Start course',
      })),
      ...lessons.map(l => ({
        key: `l-${l.id}`, title: l.title, image: l.lesson_image_url || l.cover_image_url, imageAlt: l.title,
        sub: `New lesson in ${l.course_title}`, to: `/dashboard/learning-lab/${l.course_id}/${l.id}`, cta: 'Open lesson',
      })),
    ])).catch(() => {});
  }, []);

  useEffect(() => {
    const firstLoadRequestId = dayTypeRequestRef.current;
    Promise.allSettled([
      getAnnouncement(),
      getTodayCompletions(dayType ? DAY_TYPE_ENERGY[dayType] : undefined),
      getTodayBlocks(dayType ? DAY_TYPE_ENERGY[dayType] : undefined),
      getScoreSummary(),
      getResetPath(),
      getMemberFocus(),
      getMyHubEvents(),
      courseService.listCourses(),
      getUpcomingHubEvents(),
      getMyCalendarEvents(),
    ]).then(([ann, nn, blocks, scores, path, focus, myHub, courses, upcoming, calEvents]) => {
      if (ann.status === 'fulfilled') setAnnouncement(ann.value);
      // Skip if a colour-specific refresh has started since (it has the right list).
      const colourRefreshed = dayTypeRequestRef.current !== firstLoadRequestId;
      if (nn.status === 'fulfilled' && !colourRefreshed) setNonNegotiables(nn.value);
      if (blocks.status === 'fulfilled' && !colourRefreshed) setTodayBlocks(blocks.value);
      if (scores.status === 'fulfilled') {
        const d = scores.value;
        setScoreSummary(Array.isArray(d) ? d : d.summary || []);
      }
      if (path.status === 'fulfilled') {
        setResetPath(path.value?.path || null);
      }
      if (focus.status === 'fulfilled') {
        const mf = focus.value?.member_focus || null;
        setMemberFocus(mf);
        setFocusDraft(mf || '');
        setFocusMilestone(focus.value?.focus_milestone || null);
      }
      if (myHub.status === 'fulfilled') {
        // Show only events happening today in the member's local timezone
        const memberTz = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        const todayInTz = new Date().toLocaleDateString('en-CA', { timeZone: memberTz }); // YYYY-MM-DD
        const todayEvents = (myHub.value || []).filter(ev => {
          if (!ev.hub_event_date) return false;
          const evDate = new Date(ev.hub_event_date).toLocaleDateString('en-CA', { timeZone: memberTz });
          return evDate === todayInTz;
        });
        setTodayHubActivities(todayEvents);
      }
      if (courses.status === 'fulfilled') {
        // Most recently opened, still-in-progress course — same "resume" logic as Learning Lab's own widget.
        const resumable = (courses.value || [])
          .filter(c => c.last_accessed_at && !c.locked && c.next_lesson)
          .sort((a, b) => new Date(b.last_accessed_at) - new Date(a.last_accessed_at));
        setLatestCourse(resumable[0] || null);
      }
      if (upcoming.status === 'fulfilled') {
        setNextEvent((upcoming.value || [])[0] || null);
      }
      if (calEvents.status === 'fulfilled') {
        setCalendarEvents(calEvents.value || []);
      }
      setDataLoaded(true);
    });
  }, []);

  // Two-way check-off sync with Google Calendar's Tasks: when this page opens,
  // and whenever the member comes back to this tab (e.g. after ticking
  // something off in Google Calendar), pull their latest ticks in and refresh
  // What's on Tap if anything changed. A no-op for members without the opt-in.
  const dayTypeLiveRef = useRef(null);
  dayTypeLiveRef.current = dayType;

  // The colour can be changed on another device (Jenn, Sep 19: set Yellow on
  // desktop, the phone — already open in the background — still showed the
  // old one). Re-read it whenever this page comes back on screen, and redo
  // What's on Tap if it changed.
  useEffect(() => {
    const recheckDayType = async () => {
      try {
        const serverType = (await getMyDayType()) || null;
        if (serverType === dayTypeLiveRef.current) return;
        const requestId = ++dayTypeRequestRef.current;
        setDayType(serverType);
        const energy = serverType ? DAY_TYPE_ENERGY[serverType] : undefined;
        const [blocks, nns] = await Promise.all([getTodayBlocks(energy), getTodayCompletions(energy)]);
        if (requestId === dayTypeRequestRef.current) {
          setTodayBlocks(blocks);
          setNonNegotiables(nns);
        }
      } catch { /* non-critical — keeps whatever was already shown */ }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') recheckDayType(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', recheckDayType);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', recheckDayType);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const syncAndRefresh = async () => {
      try {
        const res = await syncGoogleTasksNow();
        if (!res?.changedApp) return;
        const energy = dayTypeLiveRef.current ? DAY_TYPE_ENERGY[dayTypeLiveRef.current] : undefined;
        const [blocks, nns] = await Promise.all([getTodayBlocks(energy), getTodayCompletions(energy)]);
        setTodayBlocks(blocks);
        setNonNegotiables(nns);
      } catch { /* non-critical — the scheduled sync still runs */ }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') syncAndRefresh(); };
    syncAndRefresh();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', syncAndRefresh);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', syncAndRefresh);
    };
  }, []);

  const handleToggleNN = async (item) => {
    const newVal = !item.completed;
    if (newVal) fireHearts(); // Jenn, 2026-09-29: hearts when a member checks off something on tap
    setNonNegotiables(prev => prev.map(nn => nn.id === item.id ? { ...nn, completed: newVal } : nn));
    try {
      await toggleCompletion(item.id, newVal);
    } catch {
      setNonNegotiables(prev => prev.map(nn => nn.id === item.id ? { ...nn, completed: !newVal } : nn));
    }
  };

  const handleToggleBlock = async (item) => {
    const newVal = !item.completed;
    if (newVal) fireHearts();
    setTodayBlocks(prev => prev.map(b => b.id === item.id ? { ...b, completed: newVal } : b));
    try {
      await toggleBlockCompletion(item.id, newVal);
    } catch {
      setTodayBlocks(prev => prev.map(b => b.id === item.id ? { ...b, completed: !newVal } : b));
    }
  };

  const handleMilestoneReach = async (id, currentlyReached) => {
    try {
      const updated = currentlyReached
        ? await unmarkMilestoneReached(id)
        : await markMilestoneReached(id);
      setFocusMilestone(prev => prev?.id === id ? { ...prev, ...updated } : prev);
    } catch { /* silently fail */ }
  };

  // Build unified today list: blocks + non-negotiables (includes activity-based NNs) sorted by time
  const todayDayName = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();

  // Build a set of hub-event activity names already shown in "Today's events"
  // so we don't repeat them in "What's on tap."
  const hubEventTexts = new Set(
    todayHubActivities.map(ev => (ev.activity_name || '').trim().toLowerCase())
  );

  // Optional time of day on activities (2026-09-29) — placed among timed items at
  // roughly that part of the day, with the block's name shown instead of a clock time.
  const TIME_BLOCK_INFO = {
    early_morning: { label: '🌅 Early morning', sort: '06:00' }, morning: { label: '☀️ Morning', sort: '09:00' },
    mid_day: { label: '🌤 Mid-day', sort: '12:30' }, afternoon: { label: '🌇 Afternoon', sort: '15:30' },
    early_evening: { label: '🌆 Early evening', sort: '18:30' }, late_evening: { label: '🌙 Late evening', sort: '21:00' },
  };

  const unifiedList = [];
  todayBlocks.forEach(b => {
    unifiedList.push({
      id:        `block-${b.id}`,
      rawId:     b.id,
      text:      b.block_title,
      time:      b.start_time ? formatTime(b.start_time) : null,
      sortTime:  b.start_time || '99:99',
      completed: !!b.completed,
      type:      'block',
      canToggle: true,
    });
  });
  nonNegotiables.forEach(nn => {
    // If days_of_week is set, only show on matching days; empty array = every day
    const dow = nn.days_of_week || [];
    if (dow.length > 0 && !dow.includes(todayDayName)) return;
    // Skip if this activity is already shown in the Hub events section
    if (hubEventTexts.has((nn.item_text || '').trim().toLowerCase())) return;
    unifiedList.push({
      id:        `nn-${nn.id}`,
      rawId:     nn.id,
      text:      nn.item_text,
      time:      TIME_BLOCK_INFO[nn.time_block]?.label || null,
      sortTime:  TIME_BLOCK_INFO[nn.time_block]?.sort || '98:00',
      description: (nn.activity_description || '').trim() || null,
      completed: nn.completed,
      type:      'nn',
      canToggle: true,
    });
  });
  unifiedList.sort((a, b) => a.sortTime.localeCompare(b.sortTime));

  // Deduplicate by text: if a block and a non-negotiable have the same text,
  // keep only the block, but ONLY when it actually carries real timing info.
  // Real bug found and fixed 2026-09-18: this used to swap in ANY same-text
  // block unconditionally — an untimed block (start_time null) would still
  // silently replace a real non-negotiable with matching text, and every
  // checkbox click on it then toggled ideal_agenda_block_completions instead
  // of non_negotiable_completions, permanently shadowing the real
  // non-negotiable's own completion history. Reproduced live against a real
  // account before this fix: a genuine duplicate-text untimed block had been
  // silently absorbing every checkbox click meant for the real non-negotiable.
  const seenTexts = new Map(); // lowercase text → index in deduped
  const deduped   = [];
  for (const item of unifiedList) {
    const key = (item.text || '').trim().toLowerCase();
    if (seenTexts.has(key)) {
      const existingIdx = seenTexts.get(key);
      if (deduped[existingIdx].type === 'nn' && item.type === 'block' && item.time) {
        deduped[existingIdx] = item;
      }
    } else {
      seenTexts.set(key, deduped.length);
      deduped.push(item);
    }
  }

  const sectorsOrdered = resetPath?.priority_sectors?.length > 0
    ? resetPath.priority_sectors
    : SECTORS_DEFAULT_ORDER;

  return (
    <DashboardLayout>
      <div className="space-y-6">

        {/* My Hub logo + Greeting */}
        <div className="flex items-center gap-3">
          <img src="/myhub-logo-icon.png" alt="" className="w-12 h-12 shrink-0" />
          <div>
            <h1 className="font-serif text-2xl font-bold text-reset-brown">
              Good {getGreeting()}, {firstName}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">{today}</p>
          </div>
        </div>

        <WelcomeCard firstName={firstName} />

        {user?.show_reengagement && <ReengagementBubble firstName={firstName} />}

        {/* Thought for the Day + Learning Lab + Upcoming Events — across the page.
            Extra bottom padding + a rule below deliberately separates this
            glance-only zone from what follows, which is the member's own
            reset — their choices, not ours. Three equal cards otherwise give
            the eye nowhere obvious to land first. */}
        <div className="pb-8 border-b border-gray-200">
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${contest ? 'xl:grid-cols-4' : 'lg:grid-cols-3'} gap-4`}>
            <ThoughtForTheDayBubble announcement={announcement} />
            <LearningLabBubble course={latestCourse} />
            <UpcomingEventsBubble nextEvent={nextEvent} />
            {contest && <ContestBubble contest={contest} />}
          </div>
          {newThisWeek.length > 0 && <NewThisWeekBubble items={newThisWeek} />}
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ── LEFT COLUMN ───────────────────────────────────────────── */}
        <div className="space-y-4">

          {/* Type of day — filters What's on Tap to activities tagged for this energy level */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 relative">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">What kind of day is this?</p>
              <button
                type="button"
                onClick={() => setShowDayTypeInfo(v => !v)}
                className="text-xs italic text-reset-terracotta hover:underline"
              >
                more info
              </button>
            </div>
            {showDayTypeInfo && (
              <div className="absolute right-4 top-9 z-10 w-64 bg-reset-brown text-white text-xs leading-relaxed rounded-xl shadow-lg p-4">
                <p className="mb-2">Recognizing the energy level allows us to adjust the day's planned activities in a realistic way. (These are set in My Plan).</p>
                <p className="mb-1"><strong>Red</strong> = eat, sleep, brush my teeth only today.</p>
                <p className="mb-1"><strong>Yellow</strong> = wrap me in bubble wrap and I will do only the most important activities.</p>
                <p><strong>Green</strong> = it's a good day and my regular activities are reasonable today.</p>
                <button onClick={() => setShowDayTypeInfo(false)} className="text-white/60 hover:text-white text-[11px] mt-3">Close</button>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              {[
                { type: 'red', label: 'Red', hint: 'Low energy', dot: 'bg-red-500' },
                { type: 'yellow', label: 'Yellow', hint: 'Medium energy', dot: 'bg-amber-400' },
                { type: 'green', label: 'Green', hint: 'High energy', dot: 'bg-green-500' },
              ].map(opt => (
                <button
                  key={opt.type}
                  onClick={() => handleSetDayType(opt.type)}
                  className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border-2 transition-colors ${
                    dayType === opt.type ? 'border-reset-terracotta bg-reset-terracotta/5' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className={`w-3 h-3 rounded-full ${opt.dot}`} />
                  <span className="text-xs font-medium text-reset-brown">{opt.label}</span>
                </button>
              ))}
            </div>
            {dayType && (
              <p className="text-[11px] text-gray-400 mt-2">Showing what's on tap for a {dayType} day. Tap it again to clear.</p>
            )}
          </div>

          {/* 4. What's on tap / Setup skeleton */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 text-sm">Here's what's on tap for today:</h3>
              <button
                type="button"
                role="switch"
                aria-checked={adhdMode}
                title={adhdMode ? 'Switch to full list' : 'Switch to one-at-a-time view'}
                onClick={async () => {
                  const next = !adhdMode;
                  setAdhdMode(next);
                  updateUser({ adhd_mode: next });
                  try { await updateMemberPreferences({ adhd_mode: next }); } catch { /* non-critical */ }
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition shrink-0
                  ${adhdMode ? 'bg-amber-500' : 'bg-gray-200'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition
                  ${adhdMode ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {!dataLoaded ? (
              <div className="space-y-2 animate-pulse">
                {[1, 2, 3].map(i => <div key={i} className="h-6 bg-gray-100 rounded" />)}
              </div>
            ) : deduped.length === 0 && nonNegotiables.length === 0 && todayBlocks.length === 0 ? (
              /* First-access welcome — replaces the old setup checklist,
                 which overwhelmed brand-new members before they'd even
                 explored the Hub (Jenn, 2026-08-29). */
              <div className="space-y-3 text-sm text-gray-600">
                <p className="text-gray-700">
                  Welcome to My Hub. Explore what is available to you, and please don't feel it's necessary to
                  do everything all at once. Small, steady shifts are what holds good resets.
                </p>
                <div className="space-y-2">
                  <p>Complete your daily compass check in (visit it a few times per day to update)</p>
                  <p>
                    Do a lesson from{' '}
                    <Link to="/dashboard/learning-lab" className="text-amber-600 hover:underline">Get to Know Yourself</Link>
                    {' '}or the other courses. (PS: Get to Know Yourself walks you through creating your plan and
                    creating your continuums when it is the right time to do this).
                  </p>
                  <p>
                    <Link to="/dashboard/events" className="text-amber-600 hover:underline">Join a live event or do a replay</Link>.
                  </p>
                </div>
              </div>
            ) : adhdMode ? (
              <WhatNextCard
                items={deduped}
                onToggleNN={handleToggleNN}
                onToggleBlock={handleToggleBlock}
              />
            ) : (
              <ul className="divide-y divide-gray-50">
                {deduped.map(item => (
                  <li key={item.id} className="flex items-start gap-3 py-2">
                    {item.canToggle ? (
                      <input
                        type="checkbox"
                        checked={item.completed}
                        onChange={() => {
                          if (item.type === 'block') {
                            handleToggleBlock({ id: item.rawId, completed: item.completed });
                          } else {
                            handleToggleNN({ id: item.rawId, completed: item.completed });
                          }
                        }}
                        className="mt-1 h-5 w-5 rounded border-gray-300 text-amber-600 cursor-pointer"
                      />
                    ) : (
                      <span className="mt-1 w-5 h-5 flex-shrink-0 text-gray-300 text-sm">☐</span>
                    )}
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm ${item.completed ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                        {item.text}
                      </span>
                      {item.time && (
                        <span className="text-xs text-gray-400 ml-2">{item.time}</span>
                      )}
                      {/* The activity's own description, one tap away (Jenn, 2026-09-30/10-01) —
                          kept line-for-line so a task list typed one per line reads that way. */}
                      {item.description && (
                        <button
                          type="button"
                          onClick={() => setOpenTapId(openTapId === item.id ? null : item.id)}
                          aria-expanded={openTapId === item.id}
                          className="text-xs text-reset-terracotta hover:underline ml-2"
                        >
                          {openTapId === item.id ? 'less' : 'more'}
                        </button>
                      )}
                      {item.description && openTapId === item.id && (
                        <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{item.description}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* From the member's own connected Google Calendar (Settings ›
                My Devices) — informational only, not a checkable to-do, so
                kept visually separate from the list above. Shows nothing at
                all if the member has never connected a calendar or has
                nothing on it today, rather than an empty box. */}
            {calendarEvents.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 mb-2">From your calendar today</p>
                <ul className="space-y-1.5">
                  {calendarEvents.map(ev => (
                    <li key={ev.id} className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="text-gray-300">📅</span>
                      <span>{ev.event_title}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <MentionsBar />
            {user?.can_manage_affiliates && <AffiliateWaitingBar />}
          </div>

          {/* 4. Compass Check-In Strip — always visible */}
          <CompassStrip />
        </div>

        {/* ── RIGHT COLUMN ──────────────────────────────────────────── */}
        <div className="space-y-4">

          {/* 1. My Focus — inline editable */}
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-amber-900 text-sm">My Focus</h3>
              {!focusEditing && (
                <button
                  onClick={() => { setFocusDraft(memberFocus || ''); setFocusEditing(true); }}
                  className="text-xs text-amber-600 hover:text-amber-800 hover:underline transition"
                >
                  {memberFocus ? 'Edit' : '+ Set focus'}
                </button>
              )}
            </div>

            {focusEditing ? (
              <>
                <textarea
                  value={focusDraft}
                  onChange={e => setFocusDraft(e.target.value)}
                  placeholder="What is your overall focus right now? What matters most to you this week or month?"
                  rows={3}
                  autoFocus
                  className="w-full bg-white border border-amber-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                />
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={async () => {
                      setFocusSaving(true);
                      try {
                        await updateMemberFocus({ member_focus: focusDraft.trim() || null });
                        setMemberFocus(focusDraft.trim() || null);
                        setFocusEditing(false);
                      } catch { /* silently */ }
                      setFocusSaving(false);
                    }}
                    disabled={focusSaving}
                    className="text-xs bg-amber-600 text-white px-3 py-1.5 rounded-lg hover:bg-amber-700 transition disabled:opacity-60 font-medium"
                  >
                    {focusSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    onClick={() => setFocusEditing(false)}
                    className="text-xs text-amber-700 px-3 py-1.5 rounded-lg hover:bg-amber-100 transition"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : memberFocus ? (
              <p className="text-amber-800 text-sm leading-relaxed">{memberFocus}</p>
            ) : (
              <p className="text-amber-600 text-sm italic">
                What's your overall focus right now? Click "+ Set focus" to add it.
              </p>
            )}
          </div>

          {/* 2. My Current Milestone — member picks one in My Plan */}
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <h3 className="font-semibold text-gray-800 text-sm mb-3">My Current Milestone</h3>
            {!focusMilestone ? (
              <div>
                <p className="text-xs text-gray-400 mb-2">
                  Go to My Plan and pick the one milestone you're focused on right now.
                  It will appear here each day as your anchor.
                </p>
                <Link to="/dashboard/planning" className="text-amber-600 text-sm hover:underline">
                  Pick my focus milestone →
                </Link>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: SECTOR_META[focusMilestone.sector]?.dot || '#ccc' }}
                  />
                  <span className="text-xs text-gray-400">{SECTOR_META[focusMilestone.sector]?.label || focusMilestone.sector}</span>
                </div>
                <p className="text-sm text-gray-700 leading-snug mb-3">{focusMilestone.milestone_text}</p>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => handleMilestoneReach(focusMilestone.id, focusMilestone.is_reached)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition
                      ${focusMilestone.is_reached
                        ? 'bg-green-100 border-green-300 text-green-700'
                        : 'border-gray-300 text-gray-500 hover:border-amber-400 hover:text-amber-700'}`}
                  >
                    {focusMilestone.is_reached ? '✓ Reached!' : '○ Mark as reached'}
                  </button>
                  <Link to="/dashboard/planning" className="text-xs text-amber-600 hover:underline">
                    Change →
                  </Link>
                </div>
                {focusMilestone.is_reached && focusMilestone.reached_at && (
                  <p className="text-xs text-green-600 mt-1.5">Reached {formatDate(focusMilestone.reached_at)}</p>
                )}
              </div>
            )}
          </div>

          {/* 3. My Continuums — 3×2 grid */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-2 text-sm">My Continuums</h3>
            <div className="grid grid-cols-3 gap-2">
              {sectorsOrdered.map(sector => (
                <SectorZoneCard
                  key={sector}
                  sector={sector}
                  resetPath={resetPath}
                />
              ))}
            </div>
          </div>

        </div>
      </div>
      </div>
    </DashboardLayout>
  );
}
